import { useState } from 'react'
import { useAccount, useBalance, useReadContract } from 'wagmi'
import { parseUnits, formatUnits } from 'viem'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { LiveCountdown } from '@/components/LiveCountdown'
import { Button, Panel, SectionLabel, ToggleButton, MaxLink } from '@/components/ui'
import { ADDRESSES } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import auctionAbi from '@/lib/abis/HorizonAuction.json'
import { fmt, fmtUsd, toNumber, POLL_FAST } from '@/lib/utils'
import { useAction } from '@/hooks/useAction'
import { useHorizonAuction, AUCTION_ADDR } from '@/hooks/useHorizonAuction'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'

// The three inputs map to the three buyback-array lanes. ETH is deposited natively (wrapped to WETH).
const ASSETS = { ETH: ADDRESSES.WETH, TITANX: ADDRESSES.TITANX, X28: ADDRESSES.X28 }

// ─────────────────────────────────────────────────────────────────
// The daily AUCTION (HORIZON-AUCTION-SPEC.md), replacing the retired oracle-priced mint. Deposit
// ETH/TITANX/X28 into a 24h epoch valued in USD; at close you receive a pro-rata share of the day's HORIZON
// allotment, claimable next day, fully liquid. "Power" is the dollars you put in. The auction CONTRACT
// never prices HORIZON (allocation is pure pro-rata → the flat daily allotment is the structural cap, no
// manipulable HORIZON price in the allocation path). The big "Est. ROI" the page headlines (AURYN/Blaze/
// SHOGUN all show one) is DISPLAY-ONLY: it uses the app's existing HORIZON market price and never feeds the
// contract, so surfacing it costs nothing in security. It reads honestly negative when the day's implied
// price is above market and positive when it's a discount.
// ─────────────────────────────────────────────────────────────────

export function AuctionSection() {
  const a = useHorizonAuction()
  const live = a.auctionLive && a.oracleSet && !a.paused
  const note = !a.auctionLive
    ? 'The auction goes live at launch.'
    : !a.oracleSet
      ? 'The auction opens at launch, once the price feeds are live.'
      : a.paused
        ? 'The auction is paused.'
        : null

  return (
    <div id="auction">
      <SectionHeader
        title="Auction"
        subtitle="Deposit ETH, TITANX, or X28 into today's pool and earn your pro-rata share of the day's HORIZON. Claim the next day, fully liquid. Every deposit fuels the buyback."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {note && <Banner note={note} />}
        <EnterPanel a={a} live={live} paused={a.paused} />
        <PositionsTabs a={a} />
      </div>
    </div>
  )
}

function EnterPanel({ a, live, paused }) {
  const { address: user } = useAccount()
  const action = useAction()
  const [asset, setAsset] = useState('ETH') // 'ETH' | 'TITANX' | 'X28'
  const [amount, setAmount] = useState('')
  const assetAddr = ASSETS[asset]
  const isEth = asset === 'ETH'

  let wei = 0n
  try { wei = amount ? parseUnits(amount, 18) : 0n } catch { wei = 0n }

  const ethBal = useBalance({ address: user, query: { enabled: !!user && isEth, refetchInterval: POLL_FAST } })
  const tokBal = useReadContract({ address: assetAddr, abi: ERC20_ABI, functionName: 'balanceOf', args: [user], query: { enabled: !!user && !isEth, refetchInterval: POLL_FAST } })
  const maxWei = isEth ? (ethBal.data?.value ?? 0n) : (tokBal.data ?? 0n)

  // "Power" = the USD value of the deposit, from the auction's own valuation (X28 handled as TITANX inside).
  const power = useReadContract({
    address: AUCTION_ADDR, abi: auctionAbi, functionName: 'powerOf', args: [assetAddr, wei],
    query: { enabled: live && wei > 0n, refetchInterval: POLL_FAST },
  })
  const yourUsd = power.data ?? 0n
  const denom = a.totalUsd + yourUsd
  const share = denom > 0n ? Number(yourUsd) / Number(denom) : 0
  const estHorizon = denom > 0n ? (yourUsd * a.allot) / denom : 0n
  const has = live && wei > 0n && yourUsd > 0n
  const disabled = !has || !user || action.pending

  // Display-only ROI at the app's current HORIZON market price. Never touches the contract (the auction
  // allocates purely pro-rata), so it's a UI signal — "is entering a good deal vs buying on market" — not
  // an input. Reads negative when the day's implied price is above market, positive when it's a discount.
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const yourUsdNum = toNumber(yourUsd)
  const estHorizonUsd = hasHorizonPrice ? toNumber(estHorizon) * horizonUsd : 0
  const roi = has && hasHorizonPrice && yourUsdNum > 0 ? (estHorizonUsd / yourUsdNum - 1) * 100 : 0

  let label
  if (!live) label = paused ? 'Auction paused' : 'Opens at launch'
  else if (!user) label = 'Connect wallet'
  else if (action.pending) label = 'Working…'
  else if (wei === 0n) label = 'Enter an amount'
  else label = `Enter with ${amount} ${asset}`

  async function submit() {
    const intent = isEth
      ? { calls: [{ address: AUCTION_ADDR, abi: auctionAbi, functionName: 'enterETH', value: wei }], successMsg: `Entered today's auction with ${amount} ETH.` }
      : {
          pulls: [{ token: assetAddr, spender: AUCTION_ADDR, amount: wei }],
          calls: [{ address: AUCTION_ADDR, abi: auctionAbi, functionName: 'enter', args: [assetAddr, wei] }],
          successMsg: `Entered today's auction with ${amount} ${asset}.`,
        }
    const res = await action.run(intent)
    if (!res) return
    setAmount('')
    a.refetch()
  }

  return (
    <div style={twoCol}>
      {/* LEFT — enter today's auction */}
      <Panel style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 18, opacity: live ? 1 : 0.55 }}>
        <PillSwitch value={asset} options={['ETH', 'TITANX', 'X28']} disabled={!live} onChange={(x) => { setAsset(x); setAmount('') }} />

        <div>
          <div style={inputWrap}>
            <input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" disabled={!live} style={inputBox} />
            {live && user && maxWei > 0n && <MaxLink onClick={() => setAmount(formatUnits(maxWei, 18))} />}
          </div>
          <div style={{ textAlign: 'right', marginTop: 6, color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>
            Wallet: {user ? fmt(toNumber(maxWei), 4) : '—'} {asset}
          </div>
        </div>

        {/* Power hero — the dollars you're putting into the pool + your share of it. */}
        <div style={{ textAlign: 'center', padding: '8px 0 2px' }}>
          <div style={{ fontSize: T.fs.caption, letterSpacing: 2, textTransform: 'uppercase', color: T.textDim, fontFamily: T.fontM }}>Your power · dollars into today's pool</div>
          <div style={{ fontSize: 44, fontWeight: 700, color: T.warning, fontFamily: T.fontN, lineHeight: 1.1, margin: '6px 0', textShadow: `0 0 34px ${T.warning}55` }}>
            {has ? fmtUsd(toNumber(yourUsd)) : '—'}
          </div>
          <div style={{ fontSize: T.fs.caption, color: T.positive, fontFamily: T.fontM, fontWeight: 600 }}>
            {has ? `${(share * 100).toFixed(2)}% of today's pool` : live ? 'Enter an amount' : 'Opens at launch'}
          </div>
        </div>

        <Button onClick={submit} disabled={disabled} style={{ width: '100%', padding: '15px 18px', fontSize: T.fs.ui, marginTop: 4 }}>
          {label}
        </Button>
        {action.error && <div style={{ color: T.negative, fontSize: T.fs.caption }}>{String(action.error)}</div>}

        {a.claimableTotal > 0n && <ClaimStrip a={a} />}
      </Panel>

      {/* RIGHT — the live estimate */}
      <Panel style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 4, opacity: live ? 1 : 0.55 }}>
        <SectionLabel style={{ marginBottom: 10 }}>Your estimate</SectionLabel>
        <EstRow label="You deposit" value={has ? fmtUsd(toNumber(yourUsd)) : '—'} color={T.warning} sub={has ? `${amount} ${asset}` : null} />
        <EstRow label="Est. receive" value={has ? `${fmt(toNumber(estHorizon), 2)} HORIZON` : '—'} color={T.purple} big sub="claim next day, liquid" />

        {/* Big ROI headline — display-only, at the current HORIZON market price (AURYN/Blaze/SHOGUN style). */}
        <div style={{ textAlign: 'center', padding: '10px 0 6px', borderTop: `1px solid ${T.border}`, marginTop: 8 }}>
          <div style={{ fontSize: T.fs.caption, letterSpacing: 2, textTransform: 'uppercase', color: T.textDim, fontFamily: T.fontM }}>Est. ROI at current HORIZON price</div>
          <div style={{ fontSize: 46, fontWeight: 700, color: roi >= 0 ? T.positive : T.warning, fontFamily: T.fontN, lineHeight: 1.1, margin: '6px 0', textShadow: `0 0 32px ${roi >= 0 ? T.positive : T.warning}44` }}>
            {has && hasHorizonPrice ? `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: T.fs.caption, color: T.textDim, fontFamily: T.fontM }}>
            {has && hasHorizonPrice ? `≈ ${fmtUsd(estHorizonUsd)} of HORIZON for your ${fmtUsd(yourUsdNum)}` : 'shown once a HORIZON market price exists'}
          </div>
        </div>
        <div style={{ height: 1, background: T.border, margin: '14px 0' }} />
        <DetailRow label="Today's HORIZON up for auction" value={live ? fmt(toNumber(a.allot), 0) : '—'} />
        <DetailRow label="Total entered today" value={live ? fmtUsd(toNumber(a.totalUsd)) : '—'} />
        <DetailRow label="Your share" value={has ? `${(share * 100).toFixed(2)}%` : '—'} />
        <DetailRow label="Allotment share" value={`${a.auctionBps / 100}% of the daily budget`} />
        <DetailRow label="Deposits" value="Feed the buyback engine" />
        {live && a.epochEnd > 0 && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontFamily: T.fontM, fontSize: T.fs.caption, color: T.textMuted }}>
            <span>Today's auction closes in</span>
            <LiveCountdown endSec={a.epochEnd} nowSec={a.chainNow} style={{ color: T.accent, fontFamily: T.fontN, fontWeight: 700 }} />
          </div>
        )}
        <div style={{ marginTop: 12, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6 }}>
          An estimate at the current pool size. It moves as others enter, and your final HORIZON is fixed when
          the auction closes. HORIZON itself is never priced.
        </div>
      </Panel>
    </div>
  )
}

// Inline "ready to claim" strip inside the enter panel — the sum of every settled, unclaimed epoch.
function ClaimStrip({ a }) {
  const action = useAction()
  async function claimAll() {
    const epochs = a.claimable.map((p) => p.epoch)
    const res = await action.run({ calls: [{ address: AUCTION_ADDR, abi: auctionAbi, functionName: 'claimBatch', args: [epochs] }], successMsg: 'HORIZON claimed.' })
    if (res) a.refetch()
  }
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
      <div>
        <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>Ready to claim</div>
        <div style={{ color: T.positive, fontFamily: T.fontN, fontWeight: 700, fontSize: T.fs.heading }}>{fmt(toNumber(a.claimableTotal), 2)} HORIZON</div>
      </div>
      <Button onClick={claimAll} disabled={action.pending} style={{ padding: '10px 20px', fontSize: T.fs.ui }}>Claim all</Button>
    </div>
  )
}

// Active / Claimable / Ended tabs over the user's recent epochs.
function PositionsTabs({ a }) {
  const action = useAction()
  const [tab, setTab] = useState('active')
  const withMeta = a.positions.map((p) => ({
    ...p,
    isActive: !p.settled,
    isClaimable: p.settled && !p.claimed && p.horizon > 0n,
    isEnded: p.settled && (p.claimed || p.horizon === 0n),
  }))
  const filters = {
    active: withMeta.filter((p) => p.isActive),
    claimable: withMeta.filter((p) => p.isClaimable),
    ended: withMeta.filter((p) => p.isEnded),
  }
  const list = filters[tab]

  async function claim(epoch) {
    const res = await action.run({ calls: [{ address: AUCTION_ADDR, abi: auctionAbi, functionName: 'claim', args: [epoch] }], successMsg: 'HORIZON claimed.' })
    if (res) a.refetch()
  }

  const TABS = [['active', 'Active'], ['claimable', 'Claimable'], ['ended', 'Ended']]
  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel style={{ marginBottom: 14 }}>Your positions</SectionLabel>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {TABS.map(([k, l]) => (
          <ToggleButton key={k} active={tab === k} onClick={() => setTab(k)}>
            {l} {filters[k].length > 0 ? `(${filters[k].length})` : ''}
          </ToggleButton>
        ))}
      </div>
      {list.length === 0 ? (
        <div style={{ textAlign: 'center', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, padding: '28px 0' }}>No {tab} entries.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {list.map((p) => (
            <div key={p.epoch.toString()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
              <div>
                <div style={{ fontFamily: T.fontN, fontWeight: 700, color: T.text }}>Day #{p.epoch.toString()}{p.epoch === a.epoch ? ' · today' : ''}</div>
                <div style={{ fontFamily: T.fontM, fontSize: T.fs.caption, color: T.textDim }}>You entered {fmtUsd(toNumber(p.userUsd))}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: T.fontN, fontWeight: 700, color: p.isActive ? T.warning : T.positive }}>{fmt(toNumber(p.horizon), 2)} HORIZON</div>
                <div style={{ fontFamily: T.fontM, fontSize: T.fs.caption, color: T.textDim }}>{p.isActive ? 'estimate · closes tonight' : p.claimed ? 'claimed' : 'ready to claim'}</div>
              </div>
              {p.isClaimable && (
                <Button onClick={() => claim(p.epoch)} disabled={action.pending} style={{ padding: '8px 16px', fontSize: T.fs.caption }}>
                  Claim {fmt(toNumber(p.horizon), 2)}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

// A slim status strip shown above the (greyed) form when the auction is not yet live.
function Banner({ note }) {
  return (
    <div style={{ padding: '14px 18px', borderRadius: 12, background: T.accentDim, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent, flex: '0 0 auto' }} />
      {note}
    </div>
  )
}

function EstRow({ label, value, color, big, sub }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body }}>{label}</span>
        {sub && <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>{sub}</span>}
      </div>
      <span style={{ color, fontFamily: T.fontN, fontWeight: 700, fontSize: big ? T.fs.heading : T.fs.ui, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontFamily: T.fontM, fontSize: T.fs.caption }}>
      <span style={{ color: T.textDim }}>{label}</span>
      <span style={{ color: T.textMuted, fontWeight: 600 }}>{value}</span>
    </div>
  )
}

// A single sliding pill switch for the deposit asset (three inputs → three array lanes).
function PillSwitch({ value, options, onChange, disabled }) {
  const idx = Math.max(0, options.indexOf(value))
  const w = 100 / options.length
  return (
    <div style={{ position: 'relative', display: 'flex', background: 'rgba(0,0,0,0.25)', border: `1px solid ${T.border}`, borderRadius: 999, padding: 4, opacity: disabled ? 0.6 : 1 }}>
      <div aria-hidden style={{ position: 'absolute', top: 4, bottom: 4, left: `calc(${idx * w}% + 4px)`, width: `calc(${w}% - 8px)`, background: T.accentDim, border: `1px solid ${T.accent}`, borderRadius: 999, transition: 'left 0.2s ease' }} />
      {options.map((o) => (
        <button key={o} disabled={disabled} onClick={() => onChange(o)}
          style={{ position: 'relative', zIndex: 1, flex: 1, padding: '9px 0', background: 'transparent', border: 'none', color: value === o ? T.text : T.textDim, fontWeight: 700, fontFamily: T.fontN, fontSize: T.fs.ui, cursor: disabled ? 'default' : 'pointer' }}>
          {o}
        </button>
      ))}
    </div>
  )
}

const twoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 22, alignItems: 'start' }
const inputWrap = { position: 'relative', display: 'flex', alignItems: 'center' }
const inputBox = { width: '100%', padding: '15px 16px', borderRadius: 12, border: `1px solid ${T.border}`, background: 'rgba(0,0,0,0.22)', color: T.text, fontFamily: T.fontN, fontSize: T.fs.heading, fontWeight: 600, outline: 'none' }
